import React, { useState } from 'react';
import { 
  Palette, Copy, Check, Download, Eye, Moon, Sun, 
  Sparkles, Crown, Waves, Zap, Globe
} from 'lucide-react';

interface ColorInfo {
  name: string;
  hex: string;
  useCase: string;
}

interface ColorPalette {
  id: string;
  name: string;
  tagline: string;
  vibe: string;
  icon: React.ElementType;
  colors: ColorInfo[];
  gradient?: string;
}

const palettes: ColorPalette[] = [
  {
    id: 'midnight-luxe',
    name: 'Midnight Luxe',
    tagline: 'Dark, moody, and elegant',
    vibe: 'Discreet wealth, executive minimalism, modern elite',
    icon: Moon,
    gradient: 'from-gray-900 to-emerald-900',
    colors: [
      { name: 'Obsidian Black', hex: '#0A0A0A', useCase: 'Primary background, high-end feel' },
      { name: 'Deep Emerald', hex: '#004D40', useCase: 'Accents, buttons, headers' },
      { name: 'Gold Leaf', hex: '#D4AF37', useCase: 'Luxury highlights, icons' },
      { name: 'Smoke Gray', hex: '#7A7A7A', useCase: 'Typography, secondary UI' },
      { name: 'Pearl White', hex: '#F8F8F8', useCase: 'Clean spacing, contrast' }
    ]
  },
  {
    id: 'champagne-sands',
    name: 'Champagne Sands',
    tagline: 'Warm, soft, and inviting',
    vibe: 'Coastal elegance, barefoot luxury, quiet refinement',
    icon: Sun,
    gradient: 'from-amber-100 to-orange-200',
    colors: [
      { name: 'Champagne Beige', hex: '#F7E6D5', useCase: 'Primary background' },
      { name: 'Desert Taupe', hex: '#A18A74', useCase: 'UI components, typography' },
      { name: 'Cognac Brown', hex: '#5C4033', useCase: 'Premium accents, CTAs' },
      { name: 'Soft Coral', hex: '#E6A57E', useCase: 'Highlights, secondary buttons' },
      { name: 'Ivory White', hex: '#FFFFFA', useCase: 'Space, clean cards' }
    ]
  },
  {
    id: 'royal-graphite',
    name: 'Royal Graphite',
    tagline: 'Bold and powerful',
    vibe: 'Authority, cutting-edge innovation, understated power',
    icon: Crown,
    gradient: 'from-gray-800 to-indigo-900',
    colors: [
      { name: 'Graphite Gray', hex: '#1C1C1E', useCase: 'Dominant base' },
      { name: 'Royal Indigo', hex: '#2A2A72', useCase: 'Buttons, links, accents' },
      { name: 'Platinum Silver', hex: '#E5E5E5', useCase: 'Subtle dividers, typography highlights' },
      { name: 'Soft Slate', hex: '#8C8C96', useCase: 'Secondary UI components' },
      { name: 'Crisp White', hex: '#FFFFFF', useCase: 'Clean contrast zones' }
    ]
  },
  {
    id: 'carneiros-paradiso',
    name: 'Carneiros Paradiso',
    tagline: 'Brazilian resort luxury',
    vibe: 'Intimate paradise, barefoot exclusivity, quiet indulgence',
    icon: Waves,
    gradient: 'from-teal-600 to-emerald-500',
    colors: [
      { name: 'Deep Ocean Teal', hex: '#006D77', useCase: 'Primary, hero sections' },
      { name: 'Coconut White', hex: '#FAF9F6', useCase: 'Body background' },
      { name: 'Palm Green', hex: '#3A5A40', useCase: 'Nature-inspired accents' },
      { name: 'Sandstone Beige', hex: '#D9C5B2', useCase: 'Cards, neutral zones' },
      { name: 'Golden Sunlight', hex: '#E7B416', useCase: 'Luxury highlights, booking buttons' }
    ]
  },
  {
    id: 'aurora-noir',
    name: 'Aurora Noir',
    tagline: 'Futuristic and minimal',
    vibe: 'Modern innovation, ultra-premium SaaS, AI-powered refinement',
    icon: Zap,
    gradient: 'from-black to-blue-900',
    colors: [
      { name: 'Cosmic Black', hex: '#050505', useCase: 'Dark mode UI base' },
      { name: 'Aurora Blue', hex: '#0077FF', useCase: 'Highlights, CTAs, accents' },
      { name: 'Titanium Gray', hex: '#2B2B2B', useCase: 'Cards, dividers, components' },
      { name: 'Chrome Silver', hex: '#C0C0C0', useCase: 'Iconography, subtext' },
      { name: 'Ice White', hex: '#F5F5F5', useCase: 'Crisp contrast, spacing' }
    ]
  }
];

export default function ColorPalettes() {
  const [copiedColor, setCopiedColor] = useState<string | null>(null);
  const [selectedPalette, setSelectedPalette] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'grid' | 'card' | 'ui'>('grid');

  const copyToClipboard = (hex: string) => {
    navigator.clipboard.writeText(hex);
    setCopiedColor(hex);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  const exportPalette = (palette: ColorPalette) => {
    const cssVars = palette.colors
      .map(color => `  --${palette.id}-${color.name.toLowerCase().replace(/\s+/g, '-')}: ${color.hex};`)
      .join('\n');
    
    const css = `:root {\n${cssVars}\n}`;
    
    const blob = new Blob([css], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${palette.id}-palette.css`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Palette className="h-8 w-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Premium Color Palettes</h1>
                <p className="text-sm text-gray-600">Exclusive, luxury brand colors for high-end projects</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreviewMode('grid')}
                className={`px-3 py-1.5 rounded-lg transition-colors ${
                  previewMode === 'grid' ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setPreviewMode('card')}
                className={`px-3 py-1.5 rounded-lg transition-colors ${
                  previewMode === 'card' ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Cards
              </button>
              <button
                onClick={() => setPreviewMode('ui')}
                className={`px-3 py-1.5 rounded-lg transition-colors ${
                  previewMode === 'ui' ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                UI Preview
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="space-y-12">
          {palettes.map((palette) => {
            const Icon = palette.icon;
            const isSelected = selectedPalette === palette.id;
            
            return (
              <div 
                key={palette.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all ${
                  isSelected ? 'ring-4 ring-purple-500 ring-opacity-50' : ''
                }`}
              >
                {/* Palette Header */}
                <div className={`p-6 bg-gradient-to-r ${palette.gradient}`}>
                  <div className="flex items-start justify-between">
                    <div className="text-white">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className="h-8 w-8" />
                        <h2 className="text-3xl font-bold">{palette.name}</h2>
                      </div>
                      <p className="text-white/90 text-lg mb-1">{palette.tagline}</p>
                      <p className="text-white/75 text-sm italic">{palette.vibe}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedPalette(isSelected ? null : palette.id)}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Preview"
                      >
                        <Eye className="h-5 w-5 text-white" />
                      </button>
                      <button
                        onClick={() => exportPalette(palette)}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Export CSS"
                      >
                        <Download className="h-5 w-5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Color Display */}
                <div className="p-6">
                  {previewMode === 'grid' && (
                    <div className="grid grid-cols-5 gap-4">
                      {palette.colors.map((color) => (
                        <div key={color.hex} className="group">
                          <button
                            onClick={() => copyToClipboard(color.hex)}
                            className="w-full relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all transform hover:scale-105"
                          >
                            <div 
                              className="h-24 w-full"
                              style={{ backgroundColor: color.hex }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                              {copiedColor === color.hex ? (
                                <Check className="h-6 w-6 text-white" />
                              ) : (
                                <Copy className="h-6 w-6 text-white" />
                              )}
                            </div>
                          </button>
                          <div className="mt-2">
                            <p className="font-semibold text-sm text-gray-900">{color.name}</p>
                            <p className="text-xs text-gray-600 font-mono">{color.hex}</p>
                            <p className="text-xs text-gray-500 mt-1">{color.useCase}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {previewMode === 'card' && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {palette.colors.map((color) => (
                        <div
                          key={color.hex}
                          className="flex-shrink-0 w-32 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                        >
                          <button
                            onClick={() => copyToClipboard(color.hex)}
                            className="w-full h-32 relative group"
                            style={{ backgroundColor: color.hex }}
                          >
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                              {copiedColor === color.hex ? (
                                <Check className="h-6 w-6 text-white" />
                              ) : (
                                <Copy className="h-6 w-6 text-white" />
                              )}
                            </div>
                          </button>
                          <div className="p-3 bg-white">
                            <p className="font-bold text-xs text-gray-900 truncate">{color.name}</p>
                            <p className="text-xs text-gray-600 font-mono">{color.hex}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {previewMode === 'ui' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* UI Preview Card */}
                      <div 
                        className="rounded-lg p-6"
                        style={{ backgroundColor: palette.colors[0].hex }}
                      >
                        <div 
                          className="bg-white rounded-lg p-4"
                          style={{ backgroundColor: palette.colors[4]?.hex || '#FFFFFF' }}
                        >
                          <h3 
                            className="text-xl font-bold mb-2"
                            style={{ color: palette.colors[2]?.hex }}
                          >
                            Premium Experience
                          </h3>
                          <p 
                            className="text-sm mb-4"
                            style={{ color: palette.colors[3]?.hex }}
                          >
                            This is how your palette looks in a real UI context. Perfect for luxury brands and premium services.
                          </p>
                          <button 
                            className="px-4 py-2 rounded-lg font-semibold transition-transform hover:scale-105"
                            style={{ 
                              backgroundColor: palette.colors[1].hex,
                              color: palette.colors[4]?.hex || '#FFFFFF'
                            }}
                          >
                            Book Now
                          </button>
                        </div>
                      </div>

                      {/* Color List */}
                      <div className="space-y-3">
                        {palette.colors.map((color) => (
                          <div 
                            key={color.hex}
                            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <button
                              onClick={() => copyToClipboard(color.hex)}
                              className="w-12 h-12 rounded-lg shadow-md hover:shadow-lg transition-shadow group relative"
                              style={{ backgroundColor: color.hex }}
                            >
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                {copiedColor === color.hex ? (
                                  <Check className="h-4 w-4 text-white drop-shadow" />
                                ) : (
                                  <Copy className="h-4 w-4 text-white drop-shadow" />
                                )}
                              </div>
                            </button>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-sm">{color.name}</p>
                                <code className="text-xs bg-white px-2 py-1 rounded font-mono">{color.hex}</code>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">{color.useCase}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Recommendation Section for Selected Palette */}
                {isSelected && (
                  <div className="px-6 pb-6">
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                      <div className="flex items-start gap-3">
                        <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-purple-900 mb-1">Perfect For:</h4>
                          <p className="text-sm text-purple-700">
                            {palette.id === 'midnight-luxe' && 'Premium tech products, exclusive resorts, private banking apps, luxury e-commerce'}
                            {palette.id === 'champagne-sands' && 'Wellness brands, boutique hotels, lifestyle apps, premium hospitality'}
                            {palette.id === 'royal-graphite' && 'Enterprise software, fintech platforms, executive dashboards, B2B SaaS'}
                            {palette.id === 'carneiros-paradiso' && 'Beach resorts, eco-luxury brands, travel booking platforms, tropical destinations'}
                            {palette.id === 'aurora-noir' && 'AI products, developer tools, modern SaaS, futuristic interfaces'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Hybrid Recommendation */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
          <div className="flex items-start gap-4">
            <Globe className="h-8 w-8 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-bold mb-3">Recommended Hybrid</h3>
              <p className="text-white/90 mb-4">
                For your resort + AI projects, we recommend blending <strong>Champagne Sands</strong> with <strong>Carneiros Paradiso</strong> — 
                combining earthy minimalism with luxury tropical exclusivity.
              </p>
              <p className="text-white/80 text-sm">
                This creates a unique signature that says "barefoot luxury meets cutting-edge technology" — 
                perfect for a premium beachside resort with AI-powered guest experiences.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}